import { useCallback, useState } from 'react';
import { HttpError } from '@vagusx/clients';

/**
 * Generic helper for surfacing server validation errors next to the
 * fields they came from. The form maps server `field` names (e.g.
 * `name`, `assignToId`) to UI section ids that the JSX renders the
 * inline error under.
 *
 * Behavior:
 * - Validation/duplicate/conflict errors with field info are mapped via
 *   `fieldMap` and bucketed by the resolved UI id.
 * - Any error not tied to a visible field (or unmatched in the map) is
 *   collected as a top-level message — render it in a banner.
 * - The global toast handler still fires for the same errors; this is
 *   additive contextual feedback.
 */
export interface ServerFieldErrors<UiField extends string> {
  byField: Partial<Record<UiField, string>>;
  topError: string | null;
  /** Capture errors from a caught HttpError. Returns the parsed shape. */
  capture(err: unknown): { byField: Partial<Record<UiField, string>>; topError: string | null };
  /** Reset everything before a new submit. */
  reset(): void;
  /** Drop the error for one UI field — call from onChange handlers. */
  clearField(field: UiField): void;
}

export function useServerFieldErrors<UiField extends string>(
  fieldMap: Record<string, UiField>
): ServerFieldErrors<UiField> {
  const [byField, setByField] = useState<Partial<Record<UiField, string>>>({});
  const [topError, setTopError] = useState<string | null>(null);

  const capture = useCallback(
    (err: unknown) => {
      if (!(err instanceof HttpError) || (!err.isValidation && !err.isDuplicate)) {
        const parsed = { byField: {}, topError: null };
        setByField({});
        setTopError(null);
        return parsed;
      }
      const next: Partial<Record<UiField, string>> = {};
      let top: string | null = null;
      for (const fe of err.fieldErrors) {
        const ui = fieldMap[fe.field];
        if (!ui) {
          top = top ? `${top}; ${fe.message}` : fe.message;
        } else if (!next[ui]) {
          next[ui] = fe.message;
        }
      }
      // No mappable fields → surface the raw message at the top so the
      // user sees something contextual.
      if (Object.keys(next).length === 0 && !top) {
        top = err.message;
      }
      setByField(next);
      setTopError(top);
      return { byField: next, topError: top };
    },
    [fieldMap]
  );

  const reset = useCallback(() => {
    setByField({});
    setTopError(null);
  }, []);

  const clearField = useCallback((field: UiField) => {
    setByField((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  return { byField, topError, capture, reset, clearField };
}
